import type { ReactNode } from 'react';

import { RowList } from '../../../shared/ui/row-list';
import { Segmented } from '../../../shared/ui/segmented';
import type { TLibraryMessage } from '../api';
import { ROLE_OPTIONS } from '../lib';

export interface IMessageContentProps {
  id: string;
  index: number;
  value: string;
  invalid: boolean;
  label: string;
  onChange: (next: string) => void;
}

interface IPromptMessagesProps {
  messages: readonly TLibraryMessage[];
  onChange: (next: TLibraryMessage[]) => void;
  /** The template editor for a message: the flow's data picker, or a Library sample's. */
  renderContent: (props: IMessageContentProps) => ReactNode;
  /** Keyed as the API names fields: `messages.2.role`, `messages.0.content`, `messages`. */
  problems?: ReadonlyMap<string, string>;
  idPrefix: string;
}

const MAX_MESSAGES = 50;

// A new turn answers the one before it, so few-shot pairs are two clicks.
const nextRole = (messages: readonly TLibraryMessage[]): TLibraryMessage['role'] =>
  messages[messages.length - 1]?.role === 'user' ? 'assistant' : 'user';

export const PromptMessages = ({
  messages,
  onChange,
  renderContent,
  problems,
  idPrefix,
}: IPromptMessagesProps) => {
  const setRow = (index: number, patch: Partial<TLibraryMessage>) =>
    onChange(messages.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const whole = problems?.get('messages');

  return (
    <div className="flex flex-col gap-2">
      <RowList
        items={messages}
        onChange={onChange}
        addLabel="Message"
        minItems={1}
        maxItems={MAX_MESSAGES}
        onAdd={() => ({ role: nextRole(messages), content: '' })}
        renderRow={(row, index) => {
          const roleProblem = problems?.get(`messages.${index}.role`);
          const contentProblem = problems?.get(`messages.${index}.content`);
          const id = `${idPrefix}-${index}`;
          return (
            <div className="flex flex-col gap-1" data-field={`messages.${index}`}>
              <Segmented
                aria-label={`Message ${index + 1} is from`}
                className="self-start"
                segments={ROLE_OPTIONS}
                value={row.role}
                onChange={(role) => setRow(index, { role })}
              />
              {renderContent({
                id,
                index,
                value: row.content,
                invalid: contentProblem !== undefined,
                label: `Message ${index + 1}, ${row.role}`,
                onChange: (content) => setRow(index, { content }),
              })}
              {(roleProblem ?? contentProblem) && (
                <p className="text-meta text-danger">{roleProblem ?? contentProblem}</p>
              )}
            </div>
          );
        }}
      />
      {whole && <p className="text-meta text-danger">{whole}</p>}
    </div>
  );
};
